// Utility functions for product data handling
export const formatPrice = (price, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(price);
};

export const getProductImage = (product, index = 0) => {
  // Handle different image data formats from the database
  if (product.images) {
    if (Array.isArray(product.images)) {
      if (product.images.length > index) {
        const image = product.images[index];
        return typeof image === 'string' ? image : (image.image_url || image.url || image);
      }
    } else if (typeof product.images === 'string') {
      try {
        const parsed = JSON.parse(product.images);
        if (Array.isArray(parsed) && parsed.length > index) {
          const image = parsed[index];
          return typeof image === 'string' ? image : (image.image_url || image.url || image);
        }
      } catch (e) {
        // If parsing fails, treat as single image URL
        return product.images;
      }
    }
  }

  // Fallback to placeholder
  return `https://via.placeholder.com/400x300?text=${encodeURIComponent(product.name || 'Product Image')}`;
};

export const getAllProductImages = (product) => {
  const images = [];

  if (product.images) {
    if (Array.isArray(product.images)) {
      product.images.forEach(image => {
        const imageUrl = typeof image === 'string' ? image : (image.image_url || image.url || image);
        if (imageUrl) images.push(imageUrl);
      });
    } else if (typeof product.images === 'string') {
      try {
        const parsed = JSON.parse(product.images);
        if (Array.isArray(parsed)) {
          parsed.forEach(image => {
            const imageUrl = typeof image === 'string' ? image : (image.image_url || image.url || image);
            if (imageUrl) images.push(imageUrl);
          });
        } else if (typeof parsed === 'string') {
          images.push(parsed);
        }
      } catch (e) {
        images.push(product.images);
      }
    }
  }

  return images.length > 0 ? images : [getProductImage(product)];
};

export const getProductFeatures = (product) => {
  if (product.features) {
    if (Array.isArray(product.features)) {
      return product.features;
    } else if (typeof product.features === 'string') {
      try {
        const parsed = JSON.parse(product.features);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
  }
  return [];
};

export const getProductSpecifications = (product) => {
  if (product.specifications) {
    if (typeof product.specifications === 'object' && !Array.isArray(product.specifications)) {
      return product.specifications;
    } else if (typeof product.specifications === 'string') {
      try {
        const parsed = JSON.parse(product.specifications);
        return typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch (e) {
        return {};
      }
    }
  }
  return {};
};

export const getProductTechnicalDetails = (product) => {
  if (product.technical_details) {
    if (Array.isArray(product.technical_details)) {
      return product.technical_details;
    } else if (typeof product.technical_details === 'string') {
      try {
        const parsed = JSON.parse(product.technical_details);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
  }
  return [];
};

export const getProductPrice = (product) => {
  const salePrice = product.sale_price;
  const regularPrice = product.price;

  return {
    current: salePrice || regularPrice,
    original: salePrice ? regularPrice : null,
    isOnSale: !!(salePrice && salePrice < regularPrice),
    formatted: {
      current: formatPrice(salePrice || regularPrice),
      original: salePrice ? formatPrice(regularPrice) : null
    }
  };
};

export const truncateText = (text, maxLength = 100) => {
  if (!text || typeof text !== 'string') return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

export const getProductUrl = (product) => {
  return `/products/${product.slug || product.id}`;
};

export const getProductStatus = (product) => {
  if (!product.is_active || product.status === 'inactive') {
    return 'inactive';
  }
  if (product.status === 'draft') {
    return 'draft';
  }
  return 'active';
};

export const isProductAvailable = (product) => {
  const status = getProductStatus(product);
  return status === 'active' && (product.inventory_quantity || 0) > 0;
};

export const getStockStatus = (product) => {
  const quantity = product.inventory_quantity || 0;

  if (quantity === 0) {
    return { status: 'out_of_stock', label: 'Out of Stock', color: '#dc3545' };
  } else if (quantity <= 5) {
    return { status: 'low_stock', label: 'Low Stock', color: '#fd7e14' };
  } else {
    return { status: 'in_stock', label: 'In Stock', color: '#28a745' };
  }
};

export const formatProductForDisplay = (product) => {
  return {
    ...product,
    images: getAllProductImages(product),
    primaryImage: getProductImage(product),
    features: getProductFeatures(product),
    specifications: getProductSpecifications(product),
    technicalDetails: getProductTechnicalDetails(product),
    pricing: getProductPrice(product),
    url: getProductUrl(product),
    status: getProductStatus(product),
    isAvailable: isProductAvailable(product),
    stockStatus: getStockStatus(product),
    shortDescription: truncateText(product.short_description || product.brief_description || product.description)
  };
};

export const searchProducts = (products, query) => {
  if (!query || !Array.isArray(products)) return products;

  const searchTerm = query.toLowerCase().trim();

  return products.filter(product => {
    const searchableText = [
      product.name,
      product.brand,
      product.sku,
      product.description,
      product.short_description,
      product.brief_description
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(searchTerm);
  });
};

export const filterProductsByCategory = (products, categorySlug) => {
  if (!categorySlug || !Array.isArray(products)) return products;

  return products.filter(product => {
    return product.category_slug === categorySlug ||
           product.category_name?.toLowerCase() === categorySlug.toLowerCase();
  });
};

export const sortProducts = (products, sortBy = 'name') => {
  if (!Array.isArray(products)) return products;

  const sortedProducts = [...products];

  switch (sortBy) {
    case 'name':
      return sortedProducts.sort((a, b) => a.name.localeCompare(b.name));

    case 'price_low':
      return sortedProducts.sort((a, b) => {
        const priceA = a.sale_price || a.price || 0;
        const priceB = b.sale_price || b.price || 0;
        return priceA - priceB;
      });

    case 'price_high':
      return sortedProducts.sort((a, b) => {
        const priceA = a.sale_price || a.price || 0;
        const priceB = b.sale_price || b.price || 0;
        return priceB - priceA;
      });

    case 'newest':
      return sortedProducts.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

    case 'featured':
      return sortedProducts.sort((a, b) => {
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        return a.name.localeCompare(b.name);
      });

    default:
      return sortedProducts;
  }
};

export default {
  formatPrice,
  getProductImage,
  getAllProductImages,
  getProductFeatures,
  getProductSpecifications,
  getProductTechnicalDetails,
  getProductPrice,
  truncateText,
  getProductUrl,
  getProductStatus,
  isProductAvailable,
  getStockStatus,
  formatProductForDisplay,
  searchProducts,
  filterProductsByCategory,
  sortProducts
};
